import React from 'react';
import { Activity, CheckCircle2, AlertTriangle, Droplet, Bug } from 'lucide-react';
import { Toggle } from '../ui/toggle';
import { Section, FormField, InfoButton, DISEASE_INFO, PEST_INFO } from './InspectionShared';

const ConditionSection = ({ formData, setFormData, toggleArray, isQuickInspection, handleSectionPhoto }) => {
  return (
    <Section 
      title="Hive Condition & Health" 
      icon={<Activity className="h-6 w-6 text-emerald-600" />}
      collapsible={isQuickInspection}
      defaultCollapsed={isQuickInspection}
      onPhoto={handleSectionPhoto}
    >
      <FormField label="Hive Condition (Select all that apply)">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {[
            { name: 'Normal', icon: <CheckCircle2 className="h-5 w-5" />, color: 'lime' },
            { name: 'Brace Comb', icon: null, color: 'amber' },
            { name: 'Propolis', icon: null, color: 'amber' },
            { name: 'Normal Odour', icon: null, color: 'lime' },
            { name: 'Foul Odour', icon: <AlertTriangle className="h-5 w-5" />, color: 'red' },
            { name: 'Honey Odour', icon: <Droplet className="h-5 w-5" />, color: 'amber' }
          ].map(({ name, icon, color }) => (
            <Toggle
              key={name}
              pressed={formData.hive_condition_status.includes(name)}
              onPressedChange={() => {
                setFormData(prev => ({
                  ...prev,
                  hive_condition_status: prev.hive_condition_status.includes(name)
                    ? prev.hive_condition_status.filter(v => v !== name)
                    : [...prev.hive_condition_status, name]
                }));
              }}
              data-testid={`condition-${name.toLowerCase().replace(/\s+/g, '-')}`}
              className={`h-16 flex items-center justify-center gap-2 data-[state=on]:bg-${color}-600 data-[state=on]:text-white`}
            >
              {icon}
              <span>{name}</span>
            </Toggle>
          ))}
        </div>
      </FormField>

      <FormField label="Diseases Detected">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {['Sacbrood', 'DWV', 'EFB', 'AFB', 'Chalkbrood', 'BPMS'].map((disease) => (
            <div key={disease} className="relative">
              <InfoButton info={DISEASE_INFO[disease]} name={disease} />
              <Toggle
                pressed={formData.diseases.includes(disease)}
                onPressedChange={() => toggleArray('diseases', disease)}
                data-testid={`disease-${disease.toLowerCase()}`}
                className="h-16 w-full data-[state=on]:bg-red-600 data-[state=on]:text-white flex flex-col items-center justify-center gap-0.5"
              >
                <Bug className="h-4 w-4" />
                <span className="font-medium text-sm">{disease}</span>
                {DISEASE_INFO[disease] && (
                  <span className="text-[10px] opacity-70 leading-tight">{DISEASE_INFO[disease].fullName.split('(')[0].split('/')[0].trim()}</span>
                )}
              </Toggle>
            </div>
          ))}
        </div>
      </FormField>

      <FormField label="Pests Observed">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {['Ants', 'Mites', 'Mice', 'Wax Moths', 'SHB'].map((pest) => (
            <div key={pest} className="relative">
              <InfoButton info={PEST_INFO[pest]} name={pest} />
              <Toggle
                pressed={formData.pests.includes(pest)}
                onPressedChange={() => toggleArray('pests', pest)}
                data-testid={`pest-${pest.toLowerCase().replace(' ', '-')}`}
                className="h-16 w-full data-[state=on]:bg-red-600 data-[state=on]:text-white flex flex-col items-center justify-center gap-0.5"
              >
                <Bug className="h-4 w-4" />
                <span className="font-medium text-sm">{pest}</span>
                {PEST_INFO[pest] && (
                  <span className="text-[10px] opacity-70 leading-tight">{PEST_INFO[pest].fullName.split('(')[0].trim()}</span>
                )}
              </Toggle>
            </div>
          ))}
        </div>
      </FormField>
    </Section>
  );
};

export default ConditionSection;
